import { useState, useEffect } from 'react';
import { HelpCircle, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const InstallAppBanner = () => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Hide when any full-screen modal/overlay is open. Includes:
  // • event-reel creator, audio picker, reels page, public reel page
  // • map-menu-overlay (hamburger menu) — the install card was covering
  //   the middle "Create Overnight" tile, blocking taps. (Feb 2026)
  // • add-pitstop-sheet / add-overnight-sheet — bottom sheets.
  // • rider-identity-customizer — the look-gallery cards on /konto sit
  //   in the bottom 25% of mobile and the banner intercepted their taps.
  useEffect(() => {
    const check = () => {
      const modals = document.querySelectorAll(
        '[data-testid="event-reel-creator"], [data-testid="audio-picker-overlay"], [data-testid="reels-page"], [data-testid="reels-player-page"], [data-testid="public-reel-page"], [data-testid="map-menu-overlay"], [data-testid="add-pitstop-sheet"], [data-testid="add-overnight-sheet"], [data-testid="rider-identity-customizer"], [data-testid="back-patch-editor"], [data-testid="replay-lab-stage"], [data-testid="ride-story-editor"], [data-testid="story-editor-preview-overlay"], [data-testid="review-share-overlay"], [data-testid="post-swipe-viewer"], [data-testid="reel-player-container"]'
      );
      setModalOpen(modals.length > 0);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Don't show if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    // Don't show if dismissed recently (7 days)
    const dismissed = localStorage.getItem('baremc-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return;

    const timer = setTimeout(() => setShow(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('baremc-install-dismissed', Date.now().toString());
  };

  if (!show || modalOpen) return null;

  return (
    <div
      data-testid="install-app-banner"
      className="fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md animate-in slide-in-from-bottom-5 duration-500 pointer-events-none"
    >
      <div
        className="rounded-2xl shadow-2xl border overflow-hidden p-4 flex items-start gap-3 pointer-events-auto"
        style={{
          background: 'rgba(6, 30, 22, 0.97)',
          borderColor: 'rgba(110, 231, 183, 0.3)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center shrink-0">
          <HelpCircle className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-sm">{t('install.wantApp')}</h3>
          <p className="text-white/60 text-xs mt-1" dangerouslySetInnerHTML={{ __html: t('install.installGuide') }} />
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/40 hover:text-white/70 transition-colors p-1 shrink-0"
          data-testid="install-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default InstallAppBanner;
